/**
 * Drag and drop handler for file tree operations
 */

import type { DragState, TreeNode } from './types.js';
import { isValidMove } from './TreeBuilder.js';

export type OnDropCallback = (sourcePath: string, sourceType: 'file' | 'folder', targetFolder: string) => Promise<void>;

export class DragDropHandler {
  private dragState: DragState | null = null;
  private onDrop: OnDropCallback;
  private currentDropTarget: HTMLElement | null = null;

  constructor(onDrop: OnDropCallback) {
    this.onDrop = onDrop;
  }

  /**
   * Handle drag start on a tree item
   */
  handleDragStart(e: DragEvent, node: TreeNode): void {
    if (!e.dataTransfer) return;

    this.dragState = {
      sourcePath: node.path,
      sourceType: node.type,
      targetPath: null,
      isValid: false,
    };

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', node.path);
    e.dataTransfer.setData('application/x-leedoc-node', JSON.stringify({
      path: node.path,
      type: node.type,
    }));

    // Add dragging class after a tick to allow the ghost image
    requestAnimationFrame(() => {
      const element = e.target as HTMLElement;
      element.classList.add('dragging');
    });
  }

  /**
   * Handle drag over a potential drop target
   */
  handleDragOver(e: DragEvent, targetNode: TreeNode | null): void {
    e.preventDefault();
    if (!e.dataTransfer || !this.dragState) return;

    // Determine target folder
    let targetFolderPath: string;
    if (targetNode === null) {
      // Dropping in empty area = root
      targetFolderPath = '';
    } else if (targetNode.type === 'folder') {
      targetFolderPath = targetNode.path;
    } else {
      // Dropping on file = same folder as file
      const parts = targetNode.path.split('/');
      parts.pop();
      targetFolderPath = parts.join('/');
    }

    const validation = isValidMove(
      this.dragState.sourcePath,
      this.dragState.sourceType,
      targetFolderPath
    );

    if (validation.valid) {
      e.dataTransfer.dropEffect = 'move';
      this.dragState.targetPath = targetFolderPath;
      this.dragState.isValid = true;
    } else {
      e.dataTransfer.dropEffect = 'none';
      this.dragState.targetPath = null;
      this.dragState.isValid = false;
    }
  }

  /**
   * Handle drag enter on an element
   */
  handleDragEnter(e: DragEvent, element: HTMLElement, targetNode: TreeNode | null): void {
    if (!this.dragState) return;

    // Determine if this is a valid drop target
    let targetFolderPath: string;
    if (targetNode === null) {
      targetFolderPath = '';
    } else if (targetNode.type === 'folder') {
      targetFolderPath = targetNode.path;
    } else {
      const parts = targetNode.path.split('/');
      parts.pop();
      targetFolderPath = parts.join('/');
    }

    const validation = isValidMove(
      this.dragState.sourcePath,
      this.dragState.sourceType,
      targetFolderPath
    );

    // Remove from previous target
    if (this.currentDropTarget && this.currentDropTarget !== element) {
      this.currentDropTarget.classList.remove('drop-target');
    }

    if (validation.valid) {
      element.classList.add('drop-target');
      this.currentDropTarget = element;
    }
  }

  /**
   * Handle drag leave from an element
   */
  handleDragLeave(e: DragEvent, element: HTMLElement): void {
    // Only remove if we're actually leaving this element
    // (not just entering a child)
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget && element.contains(relatedTarget)) {
      return;
    }

    element.classList.remove('drop-target');
    if (this.currentDropTarget === element) {
      this.currentDropTarget = null;
    }
  }

  /**
   * Handle drop on a target
   */
  async handleDrop(e: DragEvent, targetNode: TreeNode | null): Promise<void> {
    e.preventDefault();
    e.stopPropagation(); // Prevent double-handling from event bubbling
    if (!this.dragState) return;

    // Capture state before cleanup (in case of async operations)
    const { sourcePath, sourceType, targetPath, isValid } = this.dragState;

    // Clean up visual states and clear drag state immediately
    this.cleanup();
    this.dragState = null;

    if (isValid && targetPath !== null) {
      await this.onDrop(sourcePath, sourceType, targetPath);
    }
  }

  /**
   * Handle drag end (cleanup)
   */
  handleDragEnd(e: DragEvent): void {
    this.cleanup();
    this.dragState = null;
  }

  /**
   * Clean up visual states
   */
  private cleanup(): void {
    document.querySelectorAll('.drop-target').forEach(el =>
      el.classList.remove('drop-target')
    );
    document.querySelectorAll('.dragging').forEach(el =>
      el.classList.remove('dragging')
    );
    this.currentDropTarget = null;
  }

  /**
   * Check if a drag operation is in progress
   */
  isDragging(): boolean {
    return this.dragState !== null;
  }
}
